import { useCallback, useEffect, useState } from "react";
import { Plus, Palmtree, Clock, Check, X, AlertTriangle, Pencil, Ban } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, StatusBadge, Empty, Panel } from "@/components/common";
import { LeaveMiniCalendar } from "@/components/SpecialDays";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate, fmtDateTime, todayIso, workingDays, hoursBetween } from "@/lib/api";
import { can } from "@/lib/perm";

const CFG = {
  leaves: { title: "Ferie", sub: "Richieste di ferie e giorni di riposo.", btn: "Richiedi ferie", icon: Palmtree, endpoint: "/leaves" },
  permissions: { title: "Permessi", sub: "Permessi orari (fasce da 15 minuti).", btn: "Richiedi permesso", icon: Clock, endpoint: "/permissions" },
};

function NewRequestDialog({ kind, open, onOpenChange, onDone, isAdmin }) {
  const t = todayIso();
  const [f, setF] = useState(kind === "leaves" ? { start_date: t, end_date: t, note: "" } : { date: t, start_time: "09:00", end_time: "11:00", reason: "" });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  useEffect(() => {
    if (kind !== "leaves" || !f.start_date || !f.end_date) return;
    api.get("/leaves/preview", { params: { start_date: f.start_date, end_date: f.end_date } }).then((r) => setPreview(r.data)).catch(() => setPreview(null));
  }, [kind, f.start_date, f.end_date]);
  const blocked = preview?.blackouts?.length > 0;
  const amount = kind === "leaves" ? `${preview ? preview.days : workingDays(f.start_date, f.end_date)} giorni lavorativi` : `${hoursBetween(f.start_time, f.end_time)} ore`;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(CFG[kind].endpoint, f);
      toast.success("Richiesta inviata");
      if (data.overlap_warning > 0) toast.warning(`Attenzione: ${data.overlap_warning} colleghi del reparto sono già in ferie nel periodo.`);
      onOpenChange(false);
      onDone();
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-request-dialog">
        <DialogHeader>
          <DialogTitle>{isAdmin ? (kind === "leaves" ? "Registra le mie ferie" : "Registra il mio permesso") : CFG[kind].btn}</DialogTitle>
          <DialogDescription>{isAdmin ? "Come amministratore la tua assenza viene registrata e approvata automaticamente." : "La richiesta verrà inviata agli amministratori per l'approvazione."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {kind === "leaves" ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Dal</Label><Input type="date" value={f.start_date} onChange={set("start_date")} required data-testid="request-start-date-input" /></div>
              <div className="space-y-1.5"><Label>Al</Label><Input type="date" value={f.end_date} min={f.start_date} onChange={set("end_date")} required data-testid="request-end-date-input" /></div>
              <div className="col-span-2"><LeaveMiniCalendar start={f.start_date} end={f.end_date} onSelect={(a, b) => setF((x) => ({ ...x, start_date: a, end_date: b }))} /></div>
            </div>
          ) : (
            <>
              <div className="space-y-1.5"><Label>Giorno</Label><Input type="date" value={f.date} onChange={set("date")} required data-testid="request-date-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Dalle</Label><Input type="time" step={900} value={f.start_time} onChange={set("start_time")} required data-testid="request-start-time-input" /></div>
                <div className="space-y-1.5"><Label>Alle</Label><Input type="time" step={900} value={f.end_time} onChange={set("end_time")} required data-testid="request-end-time-input" /></div>
              </div>
            </>
          )}
          <p className="rounded-md bg-primary/10 px-3 py-2 text-sm font-medium text-primary" data-testid="request-amount">{amount}</p>
          {kind === "leaves" && preview && (preview.holidays.length > 0 || preview.closures.length > 0) && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300" data-testid="request-holidays-info">
              Esclusi dal conteggio:{" "}
              {[...preview.holidays.map((h) => `${fmtDate(h.date)} ${h.name}`), ...preview.closures.map((c) => `chiusura "${c.title}"`)].join(" · ")}
            </div>
          )}
          {kind === "leaves" && preview?.overlap > 0 && (
            <div className="rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-xs text-sky-800 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300" data-testid="request-overlap-info">
              Nel periodo selezionato altri colleghi sono già assenti in {preview.overlap} {preview.overlap === 1 ? "giorno" : "giorni"} (in azzurro nel calendario).
            </div>
          )}
          {blocked && (
            <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300" data-testid="request-blackout-warning">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              Periodo bloccato per alta intensità di lavoro: {preview.blackouts.map((b) => `${b.title} (${fmtDate(b.start_date)} → ${fmtDate(b.end_date)})`).join(", ")}. Non è possibile richiedere ferie.
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{kind === "leaves" ? "Note" : "Motivazione"}</Label>
            <Textarea value={kind === "leaves" ? f.note : f.reason} onChange={set(kind === "leaves" ? "note" : "reason")} rows={3} data-testid="request-note-input" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy || blocked} data-testid="request-submit-button">Invia richiesta</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DecisionDialog({ kind, item, onClose, onDone }) {
  const [note, setNote] = useState("");
  const decide = async (status) => {
    try {
      await api.post(`/requests/${kind}/${item.id}/decision`, { status, admin_note: note });
      toast.success(status === "approved" ? "Richiesta approvata" : "Richiesta rifiutata");
      onClose();
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="decision-dialog">
        <DialogHeader>
          <DialogTitle>Gestisci richiesta</DialogTitle>
          <DialogDescription>{item?.user_name} · {kind === "leaves" ? `${fmtDate(item?.start_date)} → ${fmtDate(item?.end_date)} (${item?.days} gg)` : `${fmtDate(item?.date)} ${item?.start_time}–${item?.end_time}`}</DialogDescription>
        </DialogHeader>
        {(item?.note || item?.reason) && <p className="rounded-md bg-muted px-3 py-2 text-sm">{item.note || item.reason}</p>}
        <div className="space-y-1.5"><Label>Nota per il dipendente</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="decision-note-input" /></div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => decide("rejected")} data-testid="decision-reject-button"><X className="mr-1.5 h-4 w-4" />Rifiuta</Button>
          <Button onClick={() => decide("approved")} data-testid="decision-approve-button"><Check className="mr-1.5 h-4 w-4" />Approva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const HIST = { edit: "Modificata", revoke: "Revocata", own_edit: "Modificata dal dipendente", edit_rejected: "Modifica rifiutata" };

function AdminEditDialog({ kind, item, mode, own, onClose, onDone }) {
  const needNote = mode !== "update";
  const [f, setF] = useState(kind === "leaves" ? { start_date: item.start_date, end_date: item.end_date } : { date: item.date, start_time: item.start_time, end_time: item.end_time });
  const [note, setNote] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/requests/${kind}/${item.id}/${mode}`, mode === "revoke" ? { note } : { ...f, note });
      toast.success(mode === "revoke" ? "Richiesta revocata" : data.proposal ? "Modifica inviata: in attesa di conferma del dipendente" : "Richiesta modificata");
      onClose();
      onDone();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-edit-dialog">
        <DialogHeader>
          <DialogTitle>{{ edit: "Proponi modifica", revoke: "Revoca richiesta", update: "Modifica la tua richiesta" }[mode]}</DialogTitle>
          <DialogDescription>
            {item.user_name} · {kind === "leaves" ? `${fmtDate(item.start_date)} → ${fmtDate(item.end_date)} (${item.days} gg)` : `${fmtDate(item.date)} ${item.start_time}–${item.end_time}`}.{" "}
            {mode === "edit" && (own ? "La modifica sarà applicata subito." : "Il dipendente riceverà notifica ed email e dovrà confermare la modifica prima che diventi effettiva.")}
            {mode === "revoke" && "Il dipendente riceverà notifica ed email con la nota."}
            {mode === "update" && (item.status === "approved" && !own ? "La richiesta tornerà in attesa di nuova approvazione." : "Gli amministratori riceveranno una notifica.")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {mode !== "revoke" && (kind === "leaves" ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Dal</Label><Input type="date" value={f.start_date} onChange={set("start_date")} required data-testid="edit-start-date-input" /></div>
              <div className="space-y-1.5"><Label>Al</Label><Input type="date" min={f.start_date} value={f.end_date} onChange={set("end_date")} required data-testid="edit-end-date-input" /></div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Giorno</Label><Input type="date" value={f.date} onChange={set("date")} required data-testid="edit-date-input" /></div>
              <div className="space-y-1.5"><Label>Dalle</Label><Input type="time" step={900} value={f.start_time} onChange={set("start_time")} required data-testid="edit-start-time-input" /></div>
              <div className="space-y-1.5"><Label>Alle</Label><Input type="time" step={900} value={f.end_time} onChange={set("end_time")} required data-testid="edit-end-time-input" /></div>
            </div>
          ))}
          <div className="space-y-1.5">
            <Label>{needNote ? "Nota obbligatoria (motivazione)" : "Nota (opzionale)"}</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} required={needNote} minLength={needNote ? 3 : undefined} data-testid="edit-note-input" />
          </div>
          <DialogFooter>
            <Button type="submit" variant={mode === "revoke" ? "destructive" : "default"} disabled={needNote && note.trim().length < 3} data-testid="edit-submit-button">
              {{ edit: own ? "Salva modifica" : "Invia proposta", revoke: "Revoca", update: "Salva modifica" }[mode]}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function RequestsPage({ kind }) {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const canManage = can(user, "approve_requests");
  const mng = (r) => canManage && (isAdmin || r.user_id !== user.id);
  const cfg = CFG[kind];
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("pending");
  const [balance, setBalance] = useState(null);
  const [open, setOpen] = useState(false);
  const [decide, setDecide] = useState(null);
  const [adminAct, setAdminAct] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/requests/${kind}`, { params: { scope: canManage ? "all" : "mine" } });
    setItems(data);
    setBalance((await api.get("/me/balance")).data);
  }, [kind, canManage]);
  useEffect(() => { load(); }, [load]);

  const answer = async (id, accept) => {
    try {
      await api.post(`/requests/${kind}/${id}/proposal`, { accept });
      toast.success(accept ? "Modifica accettata" : "Modifica rifiutata");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const cancel = async (id) => {
    try { await api.post(`/requests/${kind}/${id}/cancel`); toast.success("Richiesta annullata"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const shown = !canManage ? items : items.filter((i) => (tab === "mine" ? i.user_id === user.id : tab === "pending" ? i.status === "pending" : i.status !== "pending"));
  const remaining = balance && (balance.leave_remaining === undefined
    ? (kind === "leaves" ? `${balance.leave_used} giorni consumati quest'anno` : `${balance.permission_used} ore consumate quest'anno`)
    : (kind === "leaves" ? `${balance.leave_remaining} giorni residui su ${balance.leave_total}` : `${balance.permission_remaining} ore residue su ${balance.permission_total}`));

  return (
    <div data-testid={`${kind}-page`}>
      <PageHeader title={cfg.title} subtitle={canManage && tab !== "mine" ? cfg.sub : remaining || cfg.sub}
        actions={<Button onClick={() => setOpen(true)} data-testid="new-request-button"><Plus className="mr-1.5 h-4 w-4" />{isAdmin ? (kind === "leaves" ? "Registra ferie" : "Registra permesso") : cfg.btn}</Button>} />
      {canManage && (
        <Tabs value={tab} onValueChange={setTab} className="mb-4">
          <TabsList>
            <TabsTrigger value="pending" data-testid="tab-pending">Da gestire ({items.filter((i) => i.status === "pending").length})</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">Storico</TabsTrigger>
            <TabsTrigger value="mine" data-testid="tab-mine">Le mie</TabsTrigger>
          </TabsList>
        </Tabs>
      )}
      {shown.length === 0 ? (
        <Empty icon={cfg.icon} text="Nessuna richiesta da mostrare." testId="requests-empty" />
      ) : (
        <Panel className="overflow-x-auto fade-up">
          <Table data-testid="requests-table">
            <TableHeader>
              <TableRow>
                {canManage && <TableHead>Dipendente</TableHead>}
                <TableHead>{kind === "leaves" ? "Periodo" : "Giorno e orario"}</TableHead>
                <TableHead>{kind === "leaves" ? "Giorni" : "Ore"}</TableHead>
                <TableHead>Note</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((r) => (
                <TableRow key={r.id} data-testid={`request-row-${r.id}`}>
                  {canManage && <TableCell className="font-medium">{r.user_name}<span className="block text-xs text-muted-foreground">{r.department}</span></TableCell>}
                  <TableCell className="whitespace-nowrap">{kind === "leaves" ? `${fmtDate(r.start_date)} → ${fmtDate(r.end_date)}` : `${fmtDate(r.date)} · ${r.start_time}–${r.end_time}`}</TableCell>
                  <TableCell>{kind === "leaves" ? r.days : r.hours}</TableCell>
                  <TableCell className="max-w-xs text-sm text-muted-foreground">
                    {r.note || r.reason || "—"}
                    {r.admin_note && r.status !== "revoked" && <span className="mt-1 block text-xs">Risposta: {r.admin_note}</span>}
                    {(r.history || []).map((h, i) => (
                      <span key={`${h.at}-${h.action}`} className="mt-1 block text-xs" data-testid={`request-history-${r.id}-${i}`}>
                        <span className="font-medium text-foreground">{HIST[h.action]}</span> da {h.action === "edit_rejected" ? h.answered_by : h.by} il {fmtDateTime(h.at)}{h.after && ` (${h.before} → ${h.after})`}{h.note && `: “${h.note}”`}
                      </span>
                    ))}
                    {r.proposal && (
                      <span className="mt-2 block rounded-md border border-sky-300 bg-sky-50 p-2 text-xs text-sky-900 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200" data-testid={`request-proposal-${r.id}`}>
                        <span className="block font-semibold">Modifica proposta da {r.proposal.by}</span>
                        {r.proposal.before} → <span className="font-semibold">{r.proposal.after}</span>
                        <span className="block">Nota: “{r.proposal.note}”</span>
                        {r.user_id === user.id ? (
                          <span className="mt-2 flex gap-2">
                            <Button size="sm" className="h-7" onClick={() => answer(r.id, true)} data-testid={`proposal-accept-${r.id}`}><Check className="mr-1 h-3.5 w-3.5" />Accetta</Button>
                            <Button size="sm" variant="outline" className="h-7" onClick={() => answer(r.id, false)} data-testid={`proposal-reject-${r.id}`}><X className="mr-1 h-3.5 w-3.5" />Rifiuta</Button>
                          </span>
                        ) : <span className="mt-1 block italic">In attesa di conferma del dipendente</span>}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={r.status} testId={`request-status-${r.id}`} />
                    {r.self_recorded && <span className="mt-1 block text-xs text-muted-foreground">Registrata dall'amministratore</span>}
                  </TableCell>
                  <TableCell className="space-x-1.5 whitespace-nowrap text-right">
                    {mng(r) && r.status === "pending" && <Button size="sm" onClick={() => setDecide(r)} data-testid={`request-manage-${r.id}`}>Gestisci</Button>}
                    {mng(r) && r.status === "approved" && !r.proposal && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setAdminAct({ r, mode: "edit" })} data-testid={`request-edit-${r.id}`}><Pencil className="mr-1 h-3.5 w-3.5" />Modifica</Button>
                        <Button size="sm" variant="outline" className="text-destructive" onClick={() => setAdminAct({ r, mode: "revoke" })} data-testid={`request-revoke-${r.id}`}><Ban className="mr-1 h-3.5 w-3.5" />Revoca</Button>
                      </>
                    )}
                    {!isAdmin && r.user_id === user.id && ["pending", "approved"].includes(r.status) && !r.proposal && <Button size="sm" variant="outline" onClick={() => setAdminAct({ r, mode: "update" })} data-testid={`request-update-${r.id}`}><Pencil className="mr-1 h-3.5 w-3.5" />Modifica</Button>}
                    {r.user_id === user.id && (r.status === "pending" || (r.self_recorded && r.status === "approved")) && <Button size="sm" variant="outline" onClick={() => cancel(r.id)} data-testid={`request-cancel-${r.id}`}>Annulla</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Panel>
      )}
      {open && <NewRequestDialog kind={kind} open={open} onOpenChange={setOpen} onDone={() => { if (canManage) setTab("mine"); load(); }} isAdmin={isAdmin} />}
      <DecisionDialog kind={kind} item={decide} onClose={() => setDecide(null)} onDone={load} />
      {adminAct && <AdminEditDialog kind={kind} item={adminAct.r} mode={adminAct.mode} own={adminAct.r.user_id === user.id} onClose={() => setAdminAct(null)} onDone={load} />}
    </div>
  );
}
